import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";

const LEFT = [{ y: 60, t: "Web App" }, { y: 188, t: "Mobile App" }, { y: 316, t: "Partner Portal" }];
const RIGHT = [{ y: 20, t: "Auth & Roles" }, { y: 102, t: "Subscriptions" }, { y: 184, t: "Workflows" }, { y: 266, t: "Payments" }, { y: 348, t: "Reports & Data" }];
const lp = (y) => `M150 ${y + 22} C 186 ${y + 22}, 180 210, 215 210`;
const rp = (y) => `M345 210 C 380 210, 374 ${y + 22}, 410 ${y + 22}`;

const Node = ({ x, y, t, w = 130 }) => (
  <g>
    <rect x={x} y={y} width={w} height="44" rx="6" fill="#0a1322" stroke="rgba(148,163,184,0.25)" />
    <rect x={x + 12} y={y + 18} width="8" height="8" rx="1.5" fill="url(#qg)" />
    <text x={x + 28} y={y + 26.5} fill="#e2e8f0" fontSize="12.5" fontFamily="Manrope" fontWeight="600">{t}</text>
  </g>
);

const Packets = ({ paths }) =>
  paths.map((d, i) => (
    <circle key={i} r="2.6" fill={i % 2 ? "#a78bfa" : "#22d3ee"}>
      <animateMotion dur={`${2.4 + (i % 3) * 0.5}s`} begin={`${i * 0.35}s`} repeatCount="indefinite" path={d} />
    </circle>
  ));

const SystemSvg = ({ animate }) => {
  const paths = [...LEFT.map((n) => lp(n.y)), ...RIGHT.map((n) => rp(n.y))];
  return (
    <svg viewBox="0 0 560 420" className="h-auto w-full" role="img" aria-label="Illustrative diagram of client applications connected through an API gateway to backend services">
      <defs>
        <linearGradient id="qg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#22d3ee" /><stop offset=".5" stopColor="#3b82f6" /><stop offset="1" stopColor="#8b5cf6" /></linearGradient>
        <linearGradient id="ql" x1="0" x2="1"><stop offset="0" stopColor="#22d3ee" stopOpacity=".5" /><stop offset="1" stopColor="#8b5cf6" stopOpacity=".5" /></linearGradient>
      </defs>
      {paths.map((d, i) => <path key={i} d={d} fill="none" stroke="url(#ql)" strokeWidth="1.2" strokeDasharray="3 4" />)}
      {LEFT.map((n) => <Node key={n.t} x={20} {...n} />)}
      {RIGHT.map((n) => <Node key={n.t} x={410} {...n} />)}
      <rect x="215" y="166" width="130" height="88" rx="8" fill="#060d1a" stroke="url(#qg)" strokeWidth="1.5" />
      <text x="230" y="192" fill="#67e8f9" fontSize="10" fontFamily="JetBrains Mono" letterSpacing="1.5">CORE</text>
      <text x="230" y="214" fill="#fff" fontSize="15" fontFamily="Manrope" fontWeight="800">API Gateway</text>
      <text x="230" y="236" fill="#94a3b8" fontSize="10.5" fontFamily="JetBrains Mono">auth · rate · route</text>
      {animate && <Packets paths={paths} />}
    </svg>
  );
};

const Chip = ({ className, z, children }) => (
  <div style={{ transform: `translateZ(${z}px)` }} className={`absolute rounded-md border border-white/10 bg-[#0a1322]/90 px-3 py-2 font-mono text-[11px] text-slate-200 shadow-2xl shadow-black/50 backdrop-blur-md ${className}`}>
    {children}
  </div>
);

export const HeroVisual = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={reset} className="relative [perspective:1400px]" data-testid="hero-visual">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <div className="relative overflow-hidden rounded-xl border border-white/10 bg-[#050b14]/80 shadow-[0_40px_120px_-30px_rgba(59,130,246,0.45)] backdrop-blur-xl">
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <div className="flex gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-slate-600" /><span className="h-2.5 w-2.5 rounded-full bg-slate-600" /><span className="h-2.5 w-2.5 rounded-full bg-slate-600" /></div>
            <span className="font-mono text-[11px] text-slate-400">system-map.illustrative</span>
          </div>
          <div className="grid-dark p-4 sm:p-6"><SystemSvg animate={!reduce} /></div>
        </div>
        <Chip z={70} className="-top-4 right-6 hidden sm:block"><span className="text-cyan-300">POST</span> /v1/orders <span className="text-emerald-300">201</span></Chip>
        <Chip z={90} className="-bottom-5 right-4 lg:-right-6"><span className="text-violet-300">workflow</span>.approve → notify</Chip>
      </motion.div>
      <p className="mt-10 font-mono text-[12px] text-slate-400">Illustrative architecture · not a client system</p>
    </div>
  );
};
